<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('purchaseOrder.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('purchaseOrder.pageTitle'),
            active: true,
          },
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div
          class="
            d-flex
            justify-content-between
            ts-items-center ts-whitespace-no-wrap ts-space-x-3
          "
        >
          <div class="ts-flex ts-items-center ts-space-x-3">
            <ts-button outline color="info" @click.prevent="addNew">
              {{ $t('add_new') }}
            </ts-button>
            <a-range-picker
              v-model="dateRange"
              :allowClear="false"
              format="DD-MM-YYYY"
              valueFormat="DD-MM-YYYY"
              style="width: 200px"
              @change="fetchData()"
            />
          </div>
          <div class="d-flex ts-space-x-3 ts-items-center">
            <ts-input
              v-tooltip="$t('purchaseOrder.search')"
              v-model="search"
              :placeholder="$t('search')"
              style="width: 250px"
              class="inline-block"
            />
            <ts-filter ref="filter">
              <div class="row">
                <div class="col-md-12">
                  <label class="">{{ $t('employeeProfile.branch') }}</label>
                  <ts-branch-filter
                    @filter="branchFilter"
                    class="ts-w-full"
                  ></ts-branch-filter>
                </div>
              </div>
              <div class="row ts-mt-4">
                <div class="col-md-12">
                  <label>{{ $t('purchaseOrder.vendor') }}</label>
                  <a-select
                    show-search
                    mode="multiple"
                    :placeholder="$t('all')"
                    v-model="vendor_id"
                    showSearch
                    :filterOption="
                      (input, option) =>
                        option.componentOptions.children[0].text
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                    "
                    :options="venders"
                    class="ts-w-full"
                  ></a-select>
                </div>
              </div>
            </ts-filter>
          </div>
        </div>
      </ts-card-wrapper>
      <div class="overflow-auto">
        <ts-table
          :columns="columns"
          :records="resources"
          :loading="loading"
          :rowGroup="{
            field: 'po_date',
          }"
        >
          <template v-slot="{ record }">
            <td>{{ record.branch.branch_name_en }}</td>
            <td>{{ record.po_number }}</td>
            <td>{{ record.reference_no }}</td>
            <td class="ts-text-right">
              <p class="ts-mb-0 ts-font-extrabold ts-text-blue-700">
                {{ sumTotalAmount(record.details) }}
              </p>
            </td>
            <td>{{ record.supplier.vendor_name }}</td>
            <td>{{ record.create_by.name }}</td>
            <td class="ts-text-center">
              <span class="badge badge-success" v-if="record.is_received"
                >Yes</span
              >
              <span class="badge badge-danger" v-else>No</span>
            </td>
            <td>{{ record.receive_by ? record.receive_by.name : null }}</td>
            <td>{{ record.received_date }}</td>
            <td class="text-center ts-space-x-3 ts-whitespace-no-wrap">
              <a
                href="#"
                v-tooltip="$t('quotation.reprint')"
                class="text-primary text-red"
                @click.prevent="reprintReport(record)"
              >
                <i class="fas fa-file-pdf"></i>
              </a>
              <a
                href="#"
                v-if="!record.is_received"
                v-tooltip="$t('edit')"
                class="text-primary"
                @click.prevent="edit(record)"
              >
                <i class="fas fa-edit"></i>
              </a>
              <a-popconfirm
                v-if="!record.is_received"
                placement="leftTop"
                :title="$t('are_you_sure_to_delete')"
                :ok-text="$t('yes')"
                :cancel-text="$t('no')"
                @confirm="deleteRecord(record)"
              >
                <a href="#" :disabled="record._deleting">
                  <i
                    class="fas fa-circle-notch spin"
                    v-if="record._deleting"
                  ></i>
                  <i class="fa fa-trash text-red" v-else></i>
                </a>
              </a-popconfirm>
            </td>
          </template>
        </ts-table>
      </div>
      <div class="d-flex justify-content-end p-15">
        <ts-pagination
          v-model="pagination"
          @navigate="fetchData"
        ></ts-pagination>
      </div>
      <a-modal :title="$t('pdf')" width="50%" v-model="show_pdf" :footer="null">
        <ts-preview-pdf v-model="waiting" :src="src" />
      </a-modal>
    </ts-card>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapState, mapActions } from 'vuex'
import moment from 'moment'

export default {
  name: 'profileIndex',
  data() {
    return {
      loading: false,
      waiting: false,
      show_pdf: false,
      vendor_id: [],
      venders: [],
      src: '',
    }
  },
  created() {
    this.$store.state.purchaseOrder.order.dateRange = [
      moment().add(-7, 'days').format('DD-MM-YYYY'),
      moment().format('DD-MM-YYYY'),
    ]
    this.fetchData()
    this.getVendor()
  },
  computed: {
    ...mapState('purchaseOrder/order', ['resources', 'pagination']),
    search: {
      get() {
        return this.$store.state.purchaseOrder.order.search
      },
      set(newValue) {
        this.$store.commit('purchaseOrder/order/SET_SEARCH', newValue)
        this.$store.commit('purchaseOrder/order/RESET_CURRENT_PAGE')
      },
    },
    dateRange: {
      get() {
        return this.$store.state.purchaseOrder.order.dateRange
      },
      set(newValue) {
        this.$store.commit('purchaseOrder/order/SET_DATE_RANGE', newValue)
      },
    },
    columns() {
      return [
        { name: this.$t('purchaseOrder.branchName') },
        { name: this.$t('purchaseOrder.poNumber') },
        { name: this.$t('purchaseOrder.reference#') },
        { name: this.$t('purchaseOrder.amount'), style: 'text-align:right' },
        { name: this.$t('purchaseOrder.vendor') },
        { name: this.$t('purchaseOrder.createdBy') },
        {
          name: this.$t('purchaseOrder.isReceived'),
          style: 'text-align:center',
        },
        { name: this.$t('purchaseOrder.receivedBy') },
        { name: this.$t('purchaseOrder.receivedDate') },
        { name: this.$t('actions'), style: 'text-align:center; width:80px' },
      ]
    },
  },
  methods: {
    fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch('purchaseOrder/order/fetch', { ...attributes })
        .then((response) => {
          this.loading = false
        })
        .catch((error) => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    addNew() {
      this.$router.push({ name: 'purchase-order-create' })
    },
    edit(record) {
      this.$store.commit('purchaseOrder/order/SET_EDIT_DATA', record)
      this.$router.push({
        name: 'purchase-order-edit',
        params: { id: record.po_id },
      })
    },
    deleteRecord(record) {
      record._deleting = true
      this.$store
        .dispatch('purchaseOrder/order/destroy', record.po_id)
        .then((response) => {
          this.$toasted.success(response.message)
          this.fetchData()
        })
        .catch((error) => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          record._deleting = false
        })
    },
    reprintReport(record) {
      this.show_pdf = true
      this.waiting = true
      this.src = ''
      this.$store
        .dispatch('purchaseOrder/order/printPdf', record.po_id)
        .then((response) => {
          this.src = response.url
        })
        .catch((error) => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    getVendor() {
      this.$store.dispatch('purchaseOrder/order/getVendor').then((response) => {
        this.venders = response.data.map((el) => ({
          value: el.vendor_id,
          label: el.vendor_name,
        }))
      })
    },
    branchFilter(value) {
      this.$store.commit('purchaseOrder/order/SET_BRANCH_ID', value)
      this.fetchData()
    },
    vendorFilter() {
      this.$store.commit('purchaseOrder/order/SET_VENDOR', this.vendor_id)
      this.fetchData()
    },
    sumTotalAmount(details = []) {
      return this.$currencyFormat(
        _.round(_.sumBy(details, 'total_after_discount'), 2)
      )
    },
  },
  watch: {
    search: debounce(function (search) {
      this.fetchData()
    }, 500),
    vendor_id: debounce(function () {
      this.vendorFilter()
    }, 500),
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('purchaseOrder/order/RESET_STATE')
    next()
  },
}
</script>
